/**
 * Deep-link parser for alerts.
 *
 * The mock publisher (and the upstream RMF core) attach a `deep_link`
 * field to each alert in the form `<kind>:<id>`. The bell + toast stack
 * routes the operator to the right screen and asks the screen to open
 * the matching drawer via a `nx:alert-deep-link` window event.
 *
 * Keeping the parser here (instead of inline in AlertBell/Toast) means
 * adding a new deep-link target is a one-file change.
 */
import type { AlertDeepLinkKind } from './alert-notifications/context';
import { splitRobotRef } from './locate';

export type { AlertDeepLinkKind } from './alert-notifications/context';

export const ALERT_DEEP_LINK_EVENT = 'nx:alert-deep-link';

export function parseDeepLink(value: string | undefined): AlertDeepLinkKind | null {
  if (!value) return null;
  const idx = value.indexOf(':');
  if (idx <= 0) return null;
  const kind = value.slice(0, idx);
  const id = value.slice(idx + 1);
  if (!id) return null;
  switch (kind) {
    case 'robot': {
      // Both spellings arrive: robot alerts carry the bare name today
      // (`alerts_engine.py` keys them on it), and locate-shaped producers
      // write `robot:<fleet>/<name>`. Either way `robotName` stays bare,
      // because that is what FleetScreen's drawer lookup matches on -- a
      // `fleet/name` there matches no robot and the drawer silently
      // never opens.
      const ref = splitRobotRef(id);
      if (!ref) return null;
      return ref.fleetName
        ? { kind: 'robot', robotName: ref.robotName, fleetName: ref.fleetName }
        : { kind: 'robot', robotName: ref.robotName };
    }
    case 'task':
      return { kind: 'task', taskId: id };
    case 'door':
      return { kind: 'door', doorName: id };
    case 'lift':
      return { kind: 'lift', liftName: id };
    default:
      return null;
  }
}

export interface AlertNavigationTarget {
  /** Path to navigate to. The consuming router takes care of the rest. */
  path: string;
  /** Optional deep-link to forward to the destination screen so it can
   *  open the matching drawer / modal on mount. */
  deepLink: AlertDeepLinkKind | null;
}

/**
 * Decide where an alert click should navigate. Falls back to `/alerts`
 * for alerts without routing metadata, so the dropdown is always a
 * valid entry point.
 */
export function navigationTargetFor(alert: {
  navigateTo?: string;
  deepLink?: string;
  robotName?: string;
  taskId?: string;
}): AlertNavigationTarget {
  // Synthesize a deep_link from the structured fields if the publisher
  // didn't fill one in.
  let deepLink = parseDeepLink(alert.deepLink);
  if (!deepLink && alert.robotName) deepLink = { kind: 'robot', robotName: alert.robotName };
  if (!deepLink && alert.taskId) deepLink = { kind: 'task', taskId: alert.taskId };

  if (alert.navigateTo) {
    return { path: alert.navigateTo, deepLink };
  }
  // Infer from the deep-link kind if the publisher didn't set navigate_to.
  if (deepLink) {
    switch (deepLink.kind) {
      case 'robot':
        return { path: '/fleet', deepLink };
      case 'task':
        return { path: '/tasks', deepLink };
      case 'door':
      case 'lift':
        return { path: '/devices', deepLink };
    }
  }
  return { path: '/alerts', deepLink: null };
}
/**
 * The robot a `robot:` link opens on Fleet: the first robot of that bare
 * name among the robots Fleet lists. Fleet's own listener matches with
 * this, and so does the Alerts table when it decides whether a row is a
 * link at all -- one lookup, so a row is never offered as a link to a
 * drawer that will not open.
 */
export function robotForDeepLink<R extends { name: string }>(
  robots: readonly R[],
  robotName: string,
): R | undefined {
  return robots.find((r) => r.name === robotName);
}

/**
 * The task a `task:` link opens on Tasks: one in the live list. A job that
 * has left it (aged into Older tasks, or removed) is not found, and Tasks
 * opens nothing for it. Shared for the reason `robotForDeepLink` is.
 */
export function taskForDeepLink<T extends { id: string }>(
  tasks: readonly T[],
  taskId: string,
): T | undefined {
  return tasks.find((t) => t.id === taskId);
}
